import { z } from 'zod';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

/**
 * Common validation schemas and utilities
 * Use these to build form validation schemas
 */

// Email validation
export const emailSchema = z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email address');

// Phone validation using libphonenumber-js (proper international validation)
export const phoneSchema = z
    .string()
    .min(1, 'Phone number is required')
    .refine(
        (value) => {
            try {
                // Try to validate as international phone number
                return isValidPhoneNumber(value);
            } catch {
                return false;
            }
        },
        'Invalid phone number'
    );

// Phone validation with specific country (e.g., 'US', 'MD', 'RO')
export const phoneSchemaWithCountry = (countryCode: string = 'MD') =>
    z
        .string()
        .min(1, 'Phone number is required')
        .refine(
            (value) => {
                try {
                    return isValidPhoneNumber(value, countryCode as any);
                } catch {
                    return false;
                }
            },
            `Invalid phone number for ${countryCode}`
        );

// Phone validation that also formats the number
export const phoneSchemaWithFormat = z
    .string()
    .min(1, 'Phone number is required')
    .transform((value) => {
        try {
            if (isValidPhoneNumber(value)) {
                const phoneNumber = parsePhoneNumber(value);
                return phoneNumber.formatInternational(); // Returns format like: +1 213 373 4253
            }
            return value;
        } catch {
            return value;
        }
    })
    .refine(
        (value) => {
            try {
                return isValidPhoneNumber(value);
            } catch {
                return false;
            }
        },
        'Invalid phone number'
    );

// Password validation
export const passwordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(20, 'Password must not more than 20 symbols')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number');

// Required string
export const requiredStringSchema = (fieldName: string) =>
    z.string().min(1, `${fieldName} is required`);

// Optional string
export const optionalStringSchema = z.string().optional();

// Required number
export const requiredNumberSchema = (fieldName: string) =>
    z.number({
        required_error: `${fieldName} is required`,
        invalid_type_error: `${fieldName} must be a number`,
    });

// Positive number
export const positiveNumberSchema = (fieldName: string) =>
    z
        .number({
            required_error: `${fieldName} is required`,
            invalid_type_error: `${fieldName} must be a number`,
        })
        .positive(`${fieldName} must be positive`);

// Date validation
export const dateSchema = (fieldName: string) =>
    z.date({
        required_error: `${fieldName} is required`,
        invalid_type_error: `${fieldName} must be a valid date`,
    });

// File validation
export const fileSchema = (
    maxSize: number = 5242880, // 5MB
    acceptedTypes: string[] = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif']
) =>
    z
        .custom<File>()
        .refine((file) => file instanceof File, 'File is required')
        .refine((file) => file.size <= maxSize, `File size should be less than ${maxSize / 1024 / 1024}MB`)
        .refine(
            (file) => acceptedTypes.includes(file.type),
            `Only ${acceptedTypes.join(', ')} files are accepted`
        );

// Multiple files validation
export const filesSchema = (
    maxSize: number = 5242880,
    acceptedTypes: string[] = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
    maxFiles: number = 5
) =>
    z
        .array(z.custom<File>())
        .min(1, 'At least one file is required')
        .max(maxFiles, `Maximum ${maxFiles} files allowed`)
        .refine(
            (files) => files.every((file) => file.size <= maxSize),
            `Each file should be less than ${maxSize / 1024 / 1024}MB`
        )
        .refine(
            (files) => files.every((file) => acceptedTypes.includes(file.type)),
            `Only ${acceptedTypes.join(', ')} files are accepted`
        );

// URL validation
export const urlSchema = z.string().url('Invalid URL format');

// Enum validation helper
export const enumSchema = <T extends string>(enumValues: readonly T[], fieldName: string) =>
    z.enum(enumValues as [T, ...T[]], {
        errorMap: () => ({ message: `Invalid ${fieldName}` }),
    });

/**
 * Example usage:
 *
 * import { z } from 'zod';
 * import { emailSchema, phoneSchema, requiredStringSchema } from './validationSchemas';
 *
 * const userSchema = z.object({
 *   firstName: requiredStringSchema('First name'),
 *   lastName: requiredStringSchema('Last name'),
 *   email: emailSchema,
 *   phone: phoneSchema,
 *   role: enumSchema(['ADMIN', 'USER'], 'Role'),
 * });
 *
 * type UserFormData = z.infer<typeof userSchema>;
 */

